import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CacheService, ServerRootService } from '@se/se.ui.html5';
import { lowerFirst } from 'lodash-es';
import { Observable, throwError, from } from 'rxjs';

import { Rhythm } from '../../models/estensa-models.model';
import {
    ESTENSA_MODULE_RADIOLOGY_COMMON_SERVICE,
    Html5CollectionMethods,
    IRadiologyCommonServiceNg1,
} from './radiology-common.service';
import { Severity, UserBase, Diagnosis, BusinessUnit } from 'estensa-module';
import { ExternalUrl, SmCertificate } from '@se/models';

export type RestControllerName = 'Consultation' | 'ECG' | 'Html5' | 'List' | 'RadiologyCommon' | 'ListWorkFlow';

export interface ConsultationRest {
    GetBusinessUnitConsultation: BusinessUnit[];
}

export interface ECGRest {
    GetExportableCaliperMeasures: any[];
    GetRhythms: Rhythm[];
    GetEcgPSConclusion: any[];
    GetReferringPhysicians: UserBase[];
}

export interface RadiologyCommonRest {
    GetSeverities: Severity[];
}

export interface ListRest {
    GetPostgraduates: UserBase[];
    GetExternalUrls: ExternalUrl[];
    GetEcgDiagnosis: Diagnosis[];
    GetObscuredAvailabilitySuggestions: any[];
    GetSmCertificateList: SmCertificate[];
}

export interface IListWorkFlow {
    GetListItems: any[];//temporaneo, bisogna capire che tipo ritorna
}

export interface RestMapping {
    Consultation: ConsultationRest;
    ECG: ECGRest;
    Html5: Html5CollectionMethods;
    List: ListRest;
    RadiologyCommon: RadiologyCommonRest;
    ListWorkFlow: IListWorkFlow;
}

// Assegna ai vari metodi dei controller un metodo HTTP (di default GET)
export type MethodMappings = {
    [C in keyof RestMapping]?: {
        [M in keyof RestMapping[C]]?: 'GET' | 'POST';
    };
};

const METHOD_MAPPINGS: MethodMappings = {
    Consultation: {
        GetBusinessUnitConsultation: 'POST',
    },
    ListWorkFlow: {
        GetListItems: 'GET',
    },
};

@Injectable({
    providedIn: 'root',
})
export class EstensaCollectionService extends ServerRootService {
    constructor(
        private cacheService: CacheService,
        private httpClient: HttpClient,
        @Inject(ESTENSA_MODULE_RADIOLOGY_COMMON_SERVICE) private radiologyCommonService: IRadiologyCommonServiceNg1,
    ) {
        super();
    }

    /**
     * Per avere l'intellisense sui metodi una volta scelto il controller.
     */
    get<C extends RestControllerName, M extends keyof RestMapping[C]>(
        controller: C,
        method: M,
    ): Observable<RestMapping[C][M]> {
        if (controller === 'Html5') {
            return this.getFromHtml5(method as any);
        }

        const url = `${this.getServerRoot()}/${controller}/${method.toString()}`;
        const cacheName = `collections.${controller}.${method.toString()}`;

        const request =
            METHOD_MAPPINGS[controller]?.[method] === 'POST'
                ? this.httpClient.post<RestMapping[C][M]>(url, {})
                : this.httpClient.get<RestMapping[C][M]>(url);

        return this.cacheService.get(cacheName, request);
    }

    /**
     * Le liste ritornate dal client AngularJS contengono tutti i metadati di Angular Resource.
     * Per evitare duplicazione dei dati, queste vengono ritornate così. Nel caso non siano utilizzabili,
     *   chiamare il metodo .toJSON()
     */
    private getFromHtml5<T extends keyof RestMapping['Html5']>(method: T): Observable<RestMapping['Html5'][T]> {
        switch (method) {
            // Liste caricate all'avvio.
            case 'GetAllProvenances':
            case 'GetBusinessUnits':
            case 'GetProvenances':
                return from(this.radiologyCommonService[lowerFirst(method)]().$promise) as any;
            // Liste sul client da caricare su richiesta.
            // ...

            // Lista sconosciuta.
            default:
                return throwError(() => new Error(`Unknown collection: Html5.${method}`));
        }
    }
}
